import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Mail, Phone, Send, CheckCircle2 } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
    { '@type': 'ListItem', position: 2, name: 'Contact', item: 'https://myschoolportal.xyz/contact' },
  ],
}
import {
  validateEmail, validateName, validateSchoolName,
  validatePhone, validateFreeText, sanitize, LIMITS,
} from '../utils/validate'

function ContactInfo({ icon, title, value, sub, href }) {
  return (
    <div className="flex items-start gap-3.5">
      <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent-strong)' }}>
        {icon}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-[var(--ink-soft)]">{title}</p>
        {href ? (
          <a href={href} className="text-sm font-bold text-ink mt-0.5 hover:text-accent transition-colors duration-150 block">{value}</a>
        ) : (
          <p className="text-sm font-bold text-ink mt-0.5">{value}</p>
        )}
        {sub && <p className="text-xs text-[var(--ink-soft)] mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

function FieldError({ msg }) {
  if (!msg) return null
  return <p className="text-xs mt-1" style={{ color: 'var(--warm-strong)' }}>{msg}</p>
}

export default function Contact() {
  const location = useLocation()
  const productFromState = location.state?.product || ''

  const [form, setForm] = useState({
    name: '',
    email: '',
    school: '',
    phone: '',
    message: productFromState ? `I am interested in: ${productFromState}\n\n` : '',
    role: 'Admin',
  })
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  function handleChange(e) {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }))
  }

  function validate() {
    const errs = {}
    const name    = validateName(form.name, 'Full Name')
    const email   = validateEmail(form.email)
    const school  = validateSchoolName(form.school, 'School Name')
    const phone   = validatePhone(form.phone)
    const message = validateFreeText(form.message, 'Message', false, LIMITS.message)
    if (name)    errs.name    = name
    if (email)   errs.email   = email
    if (school)  errs.school  = school
    if (phone)   errs.phone   = phone
    if (message) errs.message = message
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL
      const response = await fetch(`${apiUrl}/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    sanitize(form.name),
          email:   form.email.trim(),
          school:  sanitize(form.school),
          phone:   form.phone.trim(),
          message: sanitize(form.message),
          role:    form.role,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.reason || 'Failed to submit request')
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Error submitting contact form:', err)
      setError(err.message || 'Failed to submit. Please try again or email us directly.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="pb-16">
      <SEO
        title="Contact Us - Get Started with MySchoolPortal Ghana"
        description="Ready to digitise your school? We set up your workspace within 24 hours, with no setup fee. Serving schools across Ghana."
        path="/contact"
        schema={[BREADCRUMB]}
      />

      {/* Hero */}
      <section className="px-4 pt-16 pb-14 text-center bg-grid-pattern">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-ink leading-[1.1] mb-5">
            Let's get your<br />school started.
          </h1>
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
            Tell us about your school and we'll set up a personalised demo and onboarding session within 24 hours.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 py-10 border-t border-theme">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[0.85fr_1.15fr] gap-10">
          {/* Info */}
          <div>
            <h2 className="text-lg font-display font-bold text-ink mb-5">Get in touch</h2>
            <ContactInfo icon={<Mail size={16} />} title="Email" value="info@myschoolportal.xyz" sub="We'll respond to your inquiry" />

            <div className="mt-5">
              <ContactInfo
                icon={<Phone size={16} />}
                title="Call or WhatsApp"
                value="+233 59 653 9060"
                href="tel:+233596539060"
                sub="Reach us directly for urgent inquiries"
              />
            </div>

            <div className="rounded p-5 mt-8 bg-sunk">
              <p className="text-sm font-bold text-ink mb-4">What happens next?</p>
              <div className="space-y-3">
                {[
                  'We review your school requirements',
                  'Schedule a personalised 30-min demo',
                  'Configure your school account',
                  'You go live - typically same week',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full font-boxy text-xs font-bold flex items-center justify-center flex-shrink-0 text-white" style={{ background: 'var(--accent)' }}>{i + 1}</span>
                    <p className="text-sm text-[var(--ink-soft)]">{s}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="rounded p-7 border border-theme bg-surface">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)' }}>
                  <CheckCircle2 size={28} style={{ color: 'var(--accent-strong)' }} />
                </div>
                <h3 className="text-xl font-display font-bold text-ink mb-2">Message received!</h3>
                <p className="text-sm text-[var(--ink-soft)]">
                  Thanks for reaching out. Our team will contact you within 24 hours to schedule your demo.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {error && (
                  <div className="p-3 rounded text-sm" style={{ background: 'color-mix(in srgb, var(--warm) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--warm-strong) 35%, transparent)', color: 'var(--warm-strong)' }}>
                    {error}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Full Name *</label>
                    <input
                      name="name" required value={form.name} onChange={handleChange}
                      className="input"
                      style={errors.name ? { borderColor: 'var(--warm-strong)' } : {}}
                      placeholder="e.g. John Mensah"
                      maxLength={LIMITS.name}
                    />
                    <FieldError msg={errors.name} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Email Address *</label>
                    <input
                      name="email" type="email" required value={form.email} onChange={handleChange}
                      className="input"
                      style={errors.email ? { borderColor: 'var(--warm-strong)' } : {}}
                      placeholder="admin@yourschool.edu.gh"
                      maxLength={LIMITS.email}
                    />
                    <FieldError msg={errors.email} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">School Name *</label>
                    <input
                      name="school" required value={form.school} onChange={handleChange}
                      className="input"
                      style={errors.school ? { borderColor: 'var(--warm-strong)' } : {}}
                      placeholder="e.g. Greenfield Academy"
                      maxLength={LIMITS.school}
                    />
                    <FieldError msg={errors.school} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Your Role</label>
                    <select
                      name="role" value={form.role} onChange={handleChange}
                      className="input appearance-none"
                    >
                      {['Admin / Head', 'Accountant', 'ICT Staff', 'Board Member', 'Other'].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Phone / WhatsApp</label>
                  <input
                    name="phone" value={form.phone} onChange={handleChange}
                    className="input"
                    style={errors.phone ? { borderColor: 'var(--warm-strong)' } : {}}
                    placeholder="+233 50 000 0000"
                    maxLength={LIMITS.phone}
                  />
                  <FieldError msg={errors.phone} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ink-soft)] mb-1.5">Message</label>
                  <textarea
                    name="message" rows={4} value={form.message} onChange={handleChange}
                    className="input resize-none"
                    style={errors.message ? { borderColor: 'var(--warm-strong)' } : {}}
                    placeholder="Tell us about your school: number of students, modules you're interested in, current challenges."
                    maxLength={LIMITS.message}
                  />
                  <FieldError msg={errors.message} />
                </div>
                <Button
                  variant="primary"
                  className="w-full justify-center"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <><Send size={16} /> Send Message</>
                  )}
                </Button>
                <p className="text-xs text-center text-[var(--ink-soft)]">
                  By submitting, you agree to our Privacy Policy. We never share your data.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
